import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { keys } from '@/lib/queryKeys';
import {
  cancelWardRequest, createWardRequest, dispenseWardRequest, fetchAdmissionRequests, fetchWorklist, markWardRequestCollected,
  type DispenseRequestInput, type WardRequestPriority,
} from './api';

export function useAdmissionRequests(admissionId: number) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.pharmacy.wardRequests(admissionId), branchVersion],
    queryFn: () => fetchAdmissionRequests(admissionId),
    refetchInterval: 30_000,
  });
}

/** Polled: the IP pharmacy works off this list, and a STAT request shouldn't wait for a manual refresh. */
export function useWorklist() {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.pharmacy.ipWorklist(), branchVersion],
    queryFn: fetchWorklist,
    refetchInterval: 20_000,
  });
}

function useAfter() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: keys.pharmacy.wardRequestsAll() });
    qc.invalidateQueries({ queryKey: keys.pharmacy.inventory() });
    qc.invalidateQueries({ queryKey: keys.notifications.feed() });
  };
}

export function useCreateWardRequest(admissionId: number) {
  const after = useAfter();
  return useMutation({
    mutationFn: (input: { priority: WardRequestPriority; notes?: string; items: Array<{ medication_order_id: number; quantity: number }> }) =>
      createWardRequest(admissionId, input),
    onSuccess: after,
  });
}

export function useCancelWardRequest() {
  const after = useAfter();
  return useMutation({ mutationFn: ({ id, reason }: { id: number; reason: string }) => cancelWardRequest(id, reason), onSuccess: after });
}

export function useDispenseWardRequest() {
  const after = useAfter();
  return useMutation({ mutationFn: ({ id, input }: { id: number; input: DispenseRequestInput }) => dispenseWardRequest(id, input), onSuccess: after });
}

export function useMarkCollected() {
  const after = useAfter();
  return useMutation({ mutationFn: ({ id, collectedBy }: { id: number; collectedBy: string }) => markWardRequestCollected(id, collectedBy), onSuccess: after });
}
